import { AnalyticsService } from '$lib/services/analytics';
import { AOFL_PRODUCT_NAMES, AOFL_PRODUCT_UUIDS } from '$lib/utils/constants.js';
import { app, setGlobalStore } from '$lib/stores/app/store.js';
import { get } from 'svelte/store';
import { Sentry } from '$lib/services/sentry';
import { QUERY_PARAM_KEYS } from '$lib/utils/constants';

/**
 * Product Experience Manager
 * Handles setting and tracking of user product experiences (1.0 vs 2.0)
 *
 * Now implements:
 * - Sets experience based on aoflProduct name
 * - Persists experience in the global app store
 * - Logs Sentry breadcrumbs and event logs
 */
export class ProductExperienceService {
	/**
	 * Sets the product experience in the global app store based on product name
	 * @param {string} aoflProduct - The AofL product name
	 */
	static async setProductExperience(aoflProduct = AOFL_PRODUCT_NAMES.ABCMOUSE) {
		const currentApp = get(app);
		if (currentApp.global.appProduct !== aoflProduct) {
			const previousProduct = currentApp.global.appProduct ?? AOFL_PRODUCT_NAMES.ABCMOUSE;
			await setGlobalStore({ appProduct: aoflProduct });
			ProductExperienceService.trackProductExperienceSwitch(aoflProduct, previousProduct);
		} else {
			try {
				AnalyticsService.trackClientDebug({
					action: 'product_experience_noop',
					message: 'setProductExperience called with same product; no experience change applied',
					currentProduct: currentApp.global.appProduct,
					attemptedProduct: aoflProduct
				});
			} catch (error) {
				Sentry.captureException(error, {
					extra: {
						action: 'Error: setProductExperience',
						currentProduct: currentApp.global.appProduct,
						attemptedProduct: aoflProduct
					}
				});
			}
		}
	}

	/**
	 * @description Tracks the product experience switch event
	 * @param {string} aoflProduct - The product name that was switched to
	 * @param {string} previousProduct - The previous product name that was switched from
	 * @returns {Promise<void>}
	 */
	static async trackProductExperienceSwitch(aoflProduct, previousProduct) {
		Sentry.addBreadcrumb({
			category: 'product-experience',
			message: `Product experience switched to: ${aoflProduct}`,
			level: 'info',
			data: {
				action: 'product_experience_switch',
				originalProduct: previousProduct,
				newProduct: aoflProduct
			}
		});

		try {
			// Fire client debug event log
			await AnalyticsService.trackClientDebug({
				action: 'product_experience_switch',
				originalProduct: previousProduct,
				newProduct: aoflProduct
			});
		} catch (error) {
			Sentry.captureException(error, {
				extra: {
					action: 'Error: trackProductExperienceSwitch',
					originalProduct: previousProduct,
					newProduct: aoflProduct
				}
			});
		}
	}

	/**
	 * Getter for the current AOFL product name based on the app's selected product experience
	 * @returns {string}
	 */
	static get AOFL_PRODUCT_NAME() {
		return get(app)?.global.appProduct ?? AOFL_PRODUCT_NAMES.ABCMOUSE;
	}

	/**
	 * Getter for the current AOFL product UUID based on the app's selected product experience
	 * @returns {string}
	 */
	static get AOFL_PRODUCT_UUID() {
		const currentProduct = get(app)?.global.appProduct ?? AOFL_PRODUCT_NAMES.ABCMOUSE;
		return currentProduct === AOFL_PRODUCT_NAMES.ABCMOUSE_2_0
			? AOFL_PRODUCT_UUIDS.ABCMOUSE_2_0
			: AOFL_PRODUCT_UUIDS.ABCMOUSE;
	}

	/**
	 * Sets the product experience based on the product UUID
	 * @param {string} productUUID - The product UUID
	 */
	static async setProductExperienceFromProductUUID(productUUID) {
		if (productUUID === AOFL_PRODUCT_UUIDS.ABCMOUSE_2_0) {
			await ProductExperienceService.setProductExperience(AOFL_PRODUCT_NAMES.ABCMOUSE_2_0);
		} else {
			await ProductExperienceService.setProductExperience(AOFL_PRODUCT_NAMES.ABCMOUSE);
		}
	}

	/**
	 * Sets the product experience based on the TARGET_AOFL_PRODUCT_UUID or PRODUCT_UUID query param, if present.
	 * @returns {Promise<void>}
	 */
	static async setProductExperienceFromQuery() {
		const urlSearchParam = new URLSearchParams(location.search);
		let targetAoflProductUuid =
			urlSearchParam.get(QUERY_PARAM_KEYS.TARGET_AOFL_PRODUCT_UUID) ||
			urlSearchParam.get(QUERY_PARAM_KEYS.PRODUCT_UUID);

		if (targetAoflProductUuid === AOFL_PRODUCT_UUIDS.ABCMOUSE_2_0) {
			await ProductExperienceService.setProductExperience(AOFL_PRODUCT_NAMES.ABCMOUSE_2_0);
		} else if (targetAoflProductUuid === AOFL_PRODUCT_UUIDS.ABCMOUSE) {
			await ProductExperienceService.setProductExperience(AOFL_PRODUCT_NAMES.ABCMOUSE);
		}
	}
}
